import { ReportDamageStep } from "@App/Components/ClientSpecific/Bayerische/ReportDamageStep";
import { ContactUs } from "@App/Components/ContactUs";
import { BrandFilter } from "@App/Components/Filters";
import { Hero } from "@App/Components/Hero";
import { Loader } from "@App/Components/Loader";
import { Modal } from "@App/Components/Modal";
import { HomeSections } from "@App/Components/molecules/whitelabel/home/sections/HomeSections";
import { LocationStepDefinition } from "@App/Components/molecules/whitelabel/home/stepDefinitions/LocationStepDefinition";
import { Step } from "@App/Components/Stepper/Step";
import { Stepper } from "@App/Components/Stepper/Stepper";
import { TriangleDivider } from "@App/Components/TriangleDivider";
import { useServices } from "@App/hooks/api/useServices";
import { useHome } from "@App/hooks/useHome";
import { usePremiumServiceUtils } from "@App/hooks/usePremiumServiceUtils";
import { useLocationStepDefinition } from "@App/hooks/whitelabel/home/stepper/useLocationStepDefinition";
import { Benefits } from "@App/Pages/Partner/Partials/Benefits";
import { EngineTypeStep } from "@App/Pages/Partner/Partials/EngineTypeStep";
import { SecondaryTireServiceStep } from "@App/Pages/Partner/Partials/SecondaryTireServiceStep";
import { ServiceStep } from "@App/Pages/Partner/Partials/ServiceStep";
import { SubServiceStep } from "@App/Pages/Partner/Partials/SubServiceStep";
import { TireServiceStep } from "@App/Pages/Partner/Partials/TireServiceStep";
import { actionCreators } from "@App/state";
import {
  ServiceIsInspectionOrServiceIsAppointment,
  ServiceIsWheelChangeOrServiceIsAutoAboOrServiceIsWheelChangeTiresstored,
} from "@App/utils/conditionals/combinations";
import { ServiceIsInspection, ServiceIsTireStorage } from "@App/utils/conditionals/services";
import { FontSize } from "@App/utils/tailwind/fontsize";
import { bindActionCreators } from "@reduxjs/toolkit";
import { Fragment, type FunctionComponent, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";

const DAMAGE_REPORT_FORM_URL =
  "https://www.diebayerische.de/kfz-schaden-melden/kfz-schaden-online-melden/";

export type HomeProps = {
  embedded?: boolean;
  onSubmit: () => void;
};

export const Home: FunctionComponent<HomeProps> = ({ embedded = false, onSubmit }: HomeProps) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { t } = useTranslation();
  const { dismissModal } = bindActionCreators(actionCreators, dispatch);

  // step definitions
  const locationStepProps = useLocationStepDefinition(onSubmit);

  const { loading, error, services } = useServices();

  const {
    currentStep,
    setCurrentStep,
    selectBrand,
    selectEngine,
    togglePremiumServiceCarReplacementOrPickup,
    toggled,
    nextStep,
    organization,
    service,
    availableSubservices,
    availableSubservicesLoading,
    premiumServices,
    selectService,
    selectTireServiceTypeAndService,
    secondaryTireStepSelection,
    tireStepSelection,
    showSecondaryTireStep,
    bevSupport,
    brand,
    modalDismissed,
  } = useHome();

  const { toggleSingularPremiumService } = usePremiumServiceUtils();

  useEffect(() => {
    if (Number.parseInt(window.location.hash.substr(1)) > 0) {
      setCurrentStep(Number.parseInt(window.location.hash.substr(1)));
    }
  }, [location]);

  useEffect(() => {
    if (!service) {
      setCurrentStep(0);
    } else if (currentStep === 0) {
      setCurrentStep(1);
    }
  }, [service]);

  if (error) {
    console.log(error);
  }

  return (
    <Fragment>
      <Hero
        organization={organization}
        title={t("hero.title", { organization: organization.name })}
        subtitle={t("hero.subtitle", { organization: organization.name })}
        backgroundImage={
          organization.settings.hero.background_image_url ??
          "/images/brand/arval-banner-darkened.jpg"
        }
        backgroundPosition={"bg-center"}
        showPoweredBy={false}
        titleFontSize={FontSize["md:text-5xl"]}
        overlay={"bg-gradient-to-r from-black/70 via-black/40 to-black/10"}
      />
      <Modal
        open={(organization.settings.welcome_modal.display ?? true) && !modalDismissed}
        onClick={() => dismissModal(true)}
        onClose={() => dismissModal(true)}
        title={t("welcomeModal.title", { organization: organization.name })}
        buttonText={"Weiter"}
        width="max-w-3xl"
      >
        <div>{t("welcomeModal.text", { organization: organization.name })}</div>
      </Modal>
      <div className="bg-white px-5 pt-5 container">
        <Stepper currentStep={currentStep} onChange={setCurrentStep}>
          <Step
            title={t("reportDamage.title")}
            completedTitle={`${t("reportDamage.title")} `}
            completedGoBackLinkTitle={t("no")}
          >
            <ReportDamageStep
              handleYes={() => {
                window.open(DAMAGE_REPORT_FORM_URL, "_blank", "noopener,noreferrer");
              }}
              handleNo={() => setCurrentStep((prev) => prev + 1)}
            />
          </Step>
          <Step
            title={t("Waehlen Sie einen Service.")}
            subtitle={service?.pivot.subtitle ? service.pivot.subtitle : null}
            completedTitle={"Service: "}
            completedGoBackLinkTitle={
              service
                ? ServiceIsTireStorage(service)
                  ? "Reifenservice"
                  : service.pivot.display_name
                    ? service.pivot.display_name
                    : service.name
                : ""
            }
          >
            <ServiceStep
              service={service}
              services={services}
              loading={loading}
              error={error}
              selectService={selectService}
              organization={organization}
            />
          </Step>
          {availableSubservicesLoading && <Loader />}
          {ServiceIsWheelChangeOrServiceIsAutoAboOrServiceIsWheelChangeTiresstored(service) &&
            !availableSubservicesLoading && (
              <Step
                title={"Welchen Service benötigen Sie?"}
                subtitle={null}
                completedTitle={"Reifenservice" + ": "}
                completedGoBackLinkTitle={tireStepSelection}
              >
                <TireServiceStep
                  services={services}
                  selectService={selectTireServiceTypeAndService}
                  organization={organization}
                  secondary={false}
                />
              </Step>
            )}
          {showSecondaryTireStep &&
            ServiceIsWheelChangeOrServiceIsAutoAboOrServiceIsWheelChangeTiresstored(service) &&
            !availableSubservicesLoading && (
              <Step
                title={"Sind die Reifen eingelagert?"}
                subtitle={null}
                completedTitle={"Sind die Reifen eingelagert?" + ": "}
                completedGoBackLinkTitle={secondaryTireStepSelection}
              >
                <SecondaryTireServiceStep
                  services={services}
                  selectService={selectTireServiceTypeAndService}
                  organization={organization}
                  secondary={true}
                />
              </Step>
            )}
          {(availableSubservices?.length > 0 || service == null) &&
            !availableSubservicesLoading && (
              <Step
                title={t("chooseSubServices")}
                subtitle={null}
                completedTitle={"Services: "}
                completedGoBackLinkTitle={`${premiumServices.length === 0 ? "Keine Auswahl" : ""} ${premiumServices.map((selected) => `${selected.name}`)}`}
              >
                <SubServiceStep
                  availableSubservices={availableSubservices}
                  premiumServices={premiumServices}
                  toggled={toggled}
                  required={false}
                  togglePremiumServiceCarReplacementOrPickup={
                    togglePremiumServiceCarReplacementOrPickup
                  }
                  togglePremiumService={toggleSingularPremiumService}
                  nextStep={nextStep}
                />
              </Step>
            )}
          {ServiceIsInspection(service) && !availableSubservicesLoading && (
            <Step
              title={t("Antriebsart")}
              subtitle={null}
              completedTitle={`${t("Antriebsart")}: `}
              completedGoBackLinkTitle={
                bevSupport ? t("Elektro-/Hybridmotor") : t("Verbrennungsmotor")
              }
            >
              <EngineTypeStep selectEngine={selectEngine} />
            </Step>
          )}
          {service &&
            ServiceIsInspectionOrServiceIsAppointment(service) &&
            !availableSubservicesLoading && (
              <Step
                title={t("Waehlen Sie Ihre Marke.")}
                completedTitle={"Marke: "}
                completedGoBackLinkTitle={brand ? brand.name : ""}
              >
                <Fragment>
                  <BrandFilter onSubmit={selectBrand} />
                </Fragment>
              </Step>
            )}
          <LocationStepDefinition {...locationStepProps} />
        </Stepper>
        <div className="mt-24">
          <HomeSections />
        </div>
      </div>
      {!embedded && (
        <div className="bg-neutral-100">
          <TriangleDivider direction="left" background={"bg-white"} />
          <div className="px-5 py-5 container md:pb-20">
            <div className="md:grid md:grid-cols-[50%_50%]">
              <Benefits organization={organization} />
              <div className="pt-5 md:pt-0 md:ml-12">
                <ContactUs />
              </div>
            </div>
          </div>
        </div>
      )}
    </Fragment>
  );
};
